/**
 * DrawerShell — reusable spring-animated bottom sheet.
 * Theme-aware: uses Tailwind dark: variants throughout.
 */
import React from "react";
import { motion, AnimatePresence } from "motion/react";
import { X } from "lucide-react";

const SPRING = { type: "spring", stiffness: 380, damping: 38, mass: 0.9 };

const DrawerShell = ({
  open,
  onClose,
  title,
  subtitle,
  children,
  maxHeight = "85vh",
}) => {
  return (
    <AnimatePresence>
      {open && (
        <>
          {/* Backdrop */}
          <motion.div
            key="backdrop"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.22 }}
            className="fixed inset-0 z-40 bg-black/50 dark:bg-black/70 backdrop-blur-sm"
            onClick={onClose}
          />

          {/* Sheet */}
          <motion.div
            key="sheet"
            initial={{ y: "100%" }}
            animate={{ y: 0 }}
            exit={{ y: "100%" }}
            transition={SPRING}
            drag="y"
            dragConstraints={{ top: 0, bottom: 0 }}
            dragElastic={{ top: 0.05, bottom: 0.4 }}
            onDragEnd={(_, info) => {
              if (info.offset.y > 80 || info.velocity.y > 500) onClose();
            }}
            style={{ maxHeight }}
            className="fixed bottom-0 left-0 right-0 z-50 flex flex-col rounded-t-3xl
                       bg-white dark:bg-gray-950
                       border-t border-gray-200 dark:border-white/10
                       shadow-2xl overflow-hidden"
          >
            {/* Drag handle */}
            <div className="flex-shrink-0 flex flex-col items-center pt-3 pb-1 cursor-grab active:cursor-grabbing">
              <div className="w-10 h-1 rounded-full bg-gray-300 dark:bg-white/20" />
            </div>

            {/* Header */}
            <div className="flex-shrink-0 flex items-start justify-between px-5 pt-2 pb-3">
              <div>
                <h2 className="text-lg font-black tracking-tight text-gray-900 dark:text-white leading-tight">
                  {title}
                </h2>
                {subtitle && (
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5 font-medium">
                    {subtitle}
                  </p>
                )}
              </div>
              <button
                onClick={onClose}
                className="flex items-center justify-center w-8 h-8 rounded-full
                           bg-gray-100 dark:bg-white/10
                           hover:bg-gray-200 dark:hover:bg-white/20
                           transition-colors
                           text-gray-500 dark:text-gray-400
                           hover:text-gray-800 dark:hover:text-white
                           mt-0.5"
                aria-label="Close drawer"
              >
                <X size={15} />
              </button>
            </div>

            {/* Divider */}
            <div className="flex-shrink-0 h-px bg-gray-200 dark:bg-white/[0.07] mx-5" />

            {/* Scrollable content */}
            <div className="flex-1 overflow-y-auto overscroll-contain">
              {children}
              {/* Safe area spacer for iOS home indicator */}
              <div className="h-[env(safe-area-inset-bottom,16px)]" />
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

export default DrawerShell;
